/* Cozy Flooring – Dumbproof Smart Carpet Calculator */
/* Simple customer result + hidden fitter logic */

(() => {
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  const state = { unit: "metres", roomType: null, roomCounter: 0 };

  const FALLBACK_PDP_HANDLE = "aura-silver";

  const toNumber = (v) => {
    if (v === "" || v == null) return NaN;
    return parseFloat(String(v).replace(",", "."));
  };

  const feetToMetres = (ft) => ft * 0.3048;
  const convert = (v) => state.unit === "feet" ? feetToMetres(v) : v;
  const ceil01 = (n) => Math.ceil((Number(n) || 0) * 10) / 10;
  const fmt = (n, d = 1) => isFinite(n) ? Number(n).toFixed(d) : "—";

  function withTrim(L, W) {
    return {
      L: L + 0.2,
      W: W + 0.2
    };
  }

  function bestSimpleRoomSize(L, W) {
    const trimmed = withTrim(L, W);

    const options = [
      {
        width: 4,
        length: trimmed.L,
        fits: trimmed.W <= 4,
        rotated: false
      },
      {
        width: 4,
        length: trimmed.W,
        fits: trimmed.L <= 4,
        rotated: true
      },
      {
        width: 5,
        length: trimmed.L,
        fits: trimmed.W <= 5,
        rotated: false
      },
      {
        width: 5,
        length: trimmed.W,
        fits: trimmed.L <= 5,
        rotated: true
      }
    ];

    const valid = options.filter(o => o.fits);

    if (!valid.length) {
      return {
        width: 4,
        length: ceil01(Math.max(trimmed.L, trimmed.W)),
        needsCheck: true,
        message: "This room may need a join, but we’ll help plan it safely."
      };
    }

    valid.sort((a, b) => {
      if (a.width !== b.width) return a.width - b.width;
      return a.length - b.length;
    });

    const best = valid[0];

    return {
      width: best.width,
      length: ceil01(best.length),
      needsCheck: false,
      rotated: best.rotated,
      message: "This should fit comfortably with fitting allowance included."
    };
  }

  function bestStairsHallSize(data) {
    const stairWidth = ceil01(data.stairWidth + 0.2);
    const stairLength = ceil01((data.steps * data.stepDepth) + 0.2);

    const pieces = [];

    if (data.hallL > 0 && data.hallW > 0) {
      pieces.push({
        name: "Hallway",
        L: ceil01(data.hallL + 0.2),
        W: ceil01(data.hallW + 0.2)
      });
    }

    if (data.landingOn && data.landL > 0 && data.landW > 0) {
      pieces.push({
        name: "Landing",
        L: ceil01(data.landL + 0.2),
        W: ceil01(data.landW + 0.2)
      });
    }

    function tryPlan(rollWidth) {
      if (stairWidth > rollWidth) return null;

      const spareWidth = rollWidth - stairWidth;
      let usedBesideStairsLength = 0;
      let extraLength = 0;

      pieces.forEach(piece => {
        const fitsNormal = piece.W <= spareWidth;
        const fitsRotated = piece.L <= spareWidth;

        if (fitsNormal || fitsRotated) {
          const chosenLength = fitsNormal ? piece.L : piece.W;
          usedBesideStairsLength += chosenLength;
        } else {
          const bestRoom = bestSimpleRoomSize(piece.L, piece.W);
          extraLength += bestRoom.length;
        }
      });

      const mainLength = Math.max(stairLength, usedBesideStairsLength);
      const totalLength = ceil01(mainLength + extraLength);

      return {
        width: rollWidth,
        length: totalLength,
        stairLength,
        stairWidth,
        spareWidth,
        extraLength
      };
    }

    const plan4 = tryPlan(4);
    const plan5 = tryPlan(5);

    let plan = plan4 || plan5;

    if (plan4 && plan5) {
      // Prefer 4m unless 5m is clearly much better
      plan = plan4;
      if (plan5.length < plan4.length - 1.5) plan = plan5;
    }

    if (!plan) {
      return {
        width: 4,
        length: stairLength,
        needsCheck: true,
        message: "This stair layout may need a quick Cozy check before ordering."
      };
    }

    const warnings = [];

    if (data.stepDepth > 0.65) {
      warnings.push("Your step depth looks higher than normal. Double-check you measured tread + riser, not the stair width.");
    }

    if (data.stepDepth < 0.35) {
      warnings.push("Your step depth looks low. Make sure you measured tread + riser together.");
    }

    return {
      width: plan.width,
      length: plan.length,
      needsCheck: warnings.length > 0,
      warnings,
      message: warnings.length
        ? "We’ve still worked out a size, but please double-check the stair measurement."
        : "Your stairs and hallway should fit within this planned size."
    };
  }

  function saveAndGo(width, length) {
    const payload = {
      width_text: `${width}m`,
      width_m: width,
      length_m: String(length)
    };

    try {
      sessionStorage.setItem("cozy_recommended_size", JSON.stringify(payload));
    } catch (e) {}

    let targetPath = `/products/${FALLBACK_PDP_HANDLE}`;

    try {
      const last = sessionStorage.getItem("cozy_last_pdp");
      if (last && /^\/products\//.test(last)) targetPath = last;
    } catch (e) {}

    const url = new URL(targetPath, window.location.origin);
    url.searchParams.set("rec_width", width);
    url.searchParams.set("rec_length_m", length);

    window.location.assign(url.toString());
  }

  function renderCustomerResult(result, extraHtml = "") {
    const checkBox = result.needsCheck
      ? `
        <div class="result-card bad" style="margin-top:12px;">
          <strong>Quick check recommended</strong><br>
          ${result.message}
          ${result.warnings ? `<br><br>${result.warnings.join("<br>")}` : ""}
        </div>
      `
      : `
        <div class="result-card good" style="margin-top:12px;">
          <strong>Good news</strong><br>
          ${result.message}
        </div>
      `;

    return `
      <div class="result-card good" style="text-align:center;">
        <div style="font-size:.95rem;color:#6d625c;margin-bottom:6px;">Recommended carpet size</div>
        <div style="font-size:2rem;font-weight:800;color:#5a463c;">
          ${result.width}m × ${fmt(result.length, 1)}m
        </div>
        <div style="font-size:.9rem;color:#6d625c;margin-top:6px;">
          Fitting allowance included
        </div>

        <button
          class="cozy-use-recommended"
          data-width="${result.width}"
          data-length="${fmt(result.length, 1)}"
          style="margin-top:16px;background:#8b7566;color:#fff;border:none;border-radius:10px;padding:12px 18px;font-weight:700;cursor:pointer;width:100%;max-width:320px;">
          View carpets in this size
        </button>
      </div>

      ${checkBox}

      <div class="result-card" style="margin-top:12px;">
        <strong>Next steps</strong><br>
        1. Choose a carpet you like<br>
        2. Order samples if you’re unsure<br>
        3. Use the recommended size when ordering
      </div>

      ${extraHtml}
    `;
  }

  const forms = {
    "rectangular": $("#rectangularForm"),
    "l-shaped": $("#l-shapedForm"),
    "stairs-halls": $("#stairs-hallsForm"),
    "multiple": $("#multipleForm")
  };

  function setUnit(unit) {
    state.unit = unit;
    $$(".unit-label").forEach(el => el.textContent = unit === "metres" ? "(metres)" : "(feet)");
    $$(".unit-option").forEach(o => o.classList.toggle("active", o.dataset.unit === unit));
  }

  function selectRoom(type) {
    state.roomType = type;

    Object.values(forms).forEach(f => f && f.classList.remove("active"));
    if (forms[type]) forms[type].classList.add("active");

    $$(".room-type-card").forEach(c => c.classList.toggle("active", c.dataset.roomType === type));

    $("#resultsSection").classList.remove("show");
    $("#resultsSummary").innerHTML = "";
  }

  function getRectInputs() {
    const L = convert(toNumber($("#rectLength").value));
    const W = convert(toNumber($("#rectWidth").value));

    if (!(L > 0 && W > 0)) return { error: "Enter valid room measurements." };

    return { type: "rect", L, W };
  }

  function getLShapedInputs() {
    const LA = convert(toNumber($("#lAreaALength").value));
    const WA = convert(toNumber($("#lAreaAWidth").value));
    const LB = convert(toNumber($("#lAreaBLength").value));
    const WB = convert(toNumber($("#lAreaBWidth").value));

    if (![LA, WA, LB, WB].every(v => v > 0)) {
      return { error: "Enter valid measurements for both parts of the L-shaped room." };
    }

    return {
      type: "l",
      L: Math.max(LA, LB),
      W: Math.max(WA, WB),
      LA,
      WA,
      LB,
      WB
    };
  }

  function getStairsInputs() {
    const steps = parseInt($("#stairSteps").value, 10);
    const stepDepth = convert(toNumber($("#stairStepDepth").value));
    const stairWidth = convert(toNumber($("#stairWidth").value));

    const hallL = convert(toNumber($("#hallLength").value));
    const hallW = convert(toNumber($("#hallWidth").value));

    const landingOn = $("#hasLanding").checked;
    const landL = convert(toNumber($("#landingLength").value));
    const landW = convert(toNumber($("#landingWidth").value));

    if (!(steps > 0 && stepDepth > 0 && stairWidth > 0)) {
      return { error: "Enter valid stair details." };
    }

    return {
      type: "stairs",
      steps,
      stepDepth,
      stairWidth,
      hallL: hallL > 0 ? hallL : 0,
      hallW: hallW > 0 ? hallW : 0,
      landingOn,
      landL: landingOn && landL > 0 ? landL : 0,
      landW: landingOn && landW > 0 ? landW : 0
    };
  }

  function getMultipleInputs() {
    const rows = $$("#roomsContainer .room-row");
    if (!rows.length) return { error: "Add at least one room." };

    let widest = 0;
    let totalLength = 0;
    const rooms = [];

    rows.forEach(row => {
      const L = convert(toNumber($(".room-length", row).value));
      const W = convert(toNumber($(".room-width", row).value));

      if (!(L > 0 && W > 0)) return;

      const size = bestSimpleRoomSize(L, W);

      widest = Math.max(widest, size.width);
      totalLength += size.length;

      rooms.push({
        name: $(".room-name", row).value || "Room",
        L,
        W,
        size
      });
    });

    if (!rooms.length) return { error: "Enter valid measurements for every room." };

    return {
      type: "multi",
      rooms,
      result: {
        width: widest <= 4 ? 4 : 5,
        length: ceil01(totalLength),
        needsCheck: false,
        message: "We’ve combined your rooms into one simple ordering estimate."
      }
    };
  }

  function renderSummary(data) {
    const wrap = $("#resultsSummary");

    if (data.error) {
      wrap.innerHTML = `<div class="result-card bad">${data.error}</div>`;
      $("#resultsSection").classList.add("show");
      return;
    }

    let html = "";

    if (data.type === "rect") {
      const result = bestSimpleRoomSize(data.L, data.W);
      html = renderCustomerResult(result);
    }

    if (data.type === "l") {
      const result = bestSimpleRoomSize(data.L, data.W);
      result.needsCheck = true;
      result.message = "L-shaped rooms can be slightly awkward, but this gives you a safe starting size.";
      html = renderCustomerResult(result);
    }

    if (data.type === "stairs") {
      const result = bestStairsHallSize(data);
      html = renderCustomerResult(result);
    }

    if (data.type === "multi") {
      html = renderCustomerResult(data.result, `
        <div class="result-card" style="margin-top:12px;">
          <strong>Room breakdown</strong><br>
          ${data.rooms.map(r => `${r.name}: ${r.size.width}m × ${fmt(r.size.length, 1)}m`).join("<br>")}
        </div>
      `);
    }

    wrap.innerHTML = html;
    $("#resultsSection").classList.add("show");

    $$(".cozy-use-recommended").forEach(btn => {
      btn.onclick = () => saveAndGo(btn.dataset.width, btn.dataset.length);
    });
  }

  function bind() {
    $$(".room-type-card").forEach(card => {
      card.addEventListener("click", () => selectRoom(card.dataset.roomType));
    });

    $$(".unit-option").forEach(option => {
      option.addEventListener("click", () => setUnit(option.dataset.unit));
    });

    $$(".guide-toggle").forEach(btn => {
      btn.addEventListener("click", () => {
        const target = $("#" + btn.dataset.target);
        if (!target) return;

        target.classList.toggle("open");
        btn.textContent = target.classList.contains("open") ? "Hide Guide" : "Show Guide";
      });
    });

    if (forms["rectangular"]) $(".calculate-btn", forms["rectangular"]).onclick = () => renderSummary(getRectInputs());
    if (forms["l-shaped"]) $(".calculate-btn", forms["l-shaped"]).onclick = () => renderSummary(getLShapedInputs());
    if (forms["stairs-halls"]) $(".calculate-btn", forms["stairs-halls"]).onclick = () => renderSummary(getStairsInputs());
    if (forms["multiple"]) $(".calculate-btn", forms["multiple"]).onclick = () => renderSummary(getMultipleInputs());

    window.addRoom = function () {
      state.roomCounter++;

      const block = document.createElement("div");
      block.className = "room-row result-card";

      block.innerHTML = `
        <div class="form-group">
          <div class="form-field">
            <label>Room name</label>
            <input class="room-name" placeholder="e.g. Bedroom">
          </div>
          <div class="form-field">
            <label>Length</label>
            <input type="number" class="room-length" step="0.1" min="0.1" placeholder="e.g. 4.2">
          </div>
          <div class="form-field">
            <label>Width</label>
            <input type="number" class="room-width" step="0.1" min="0.1" placeholder="e.g. 3.5">
          </div>
          <div class="form-field">
            <button type="button" onclick="this.closest('.room-row').remove()">Remove</button>
          </div>
        </div>
      `;

      $("#roomsContainer").appendChild(block);
    };

    const hasLanding = $("#hasLanding");
    const landingBlock = $("#landingBlock");

    if (hasLanding && landingBlock) {
      hasLanding.addEventListener("change", () => {
        landingBlock.style.display = hasLanding.checked ? "grid" : "none";
      });
    }

    setUnit("metres");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bind);
  } else {
    bind();
  }
})();